/**
 * Input encoding — quantized at the boundary so the sim never sees a raw
 * float. Fixed for this phase: changing the encoding bumps
 * REPLAY_FORMAT_VERSION.
 *
 * Layout: packed = (mag << 8) | dir   — 12 bits used of a uint16.
 * Bits 12-15 are reserved and MUST be zero. A non-zero reserved field is
 * a decode error rather than something to mask away, so a future field
 * can never be silently ignored by an older engine.
 */

export const DIR_STEPS = 256;
export const MAG_MAX = 15;

export type Command =
  | { readonly k: 'levelupChoice'; readonly index: number }
  | { readonly k: 'gateChoice'; readonly index: number }
  | { readonly k: 'reroll' }
  | { readonly k: 'revive' }
  | { readonly k: 'quit' }
  | { readonly k: 'dodge' };

export interface TickInput {
  readonly dir: number;
  readonly mag: number;
  readonly command?: Command;
}

export interface InputLog {
  /** Dense: index IS the tick. No `tick` field — that would be a second
   *  source of truth for the same fact. */
  readonly frames: Uint16Array;
  readonly commands: readonly { readonly tick: number; readonly command: Command }[];
}

export function encodeInput(dir: number, mag: number): number {
  if (!Number.isInteger(dir) || dir < 0 || dir >= DIR_STEPS) {
    throw new RangeError(`dir must be an integer in [0, ${DIR_STEPS}), got ${dir}`);
  }
  if (!Number.isInteger(mag) || mag < 0 || mag > MAG_MAX) {
    throw new RangeError(`mag must be an integer in [0, ${MAG_MAX}], got ${mag}`);
  }
  return ((mag << 8) | dir) & 0xffff;
}

export function decodeInput(packed: number): { dir: number; mag: number } {
  if (!Number.isInteger(packed) || packed < 0 || packed > 0xffff) {
    throw new RangeError(`packed frame must be a uint16, got ${packed}`);
  }
  if (packed >>> 12 !== 0) {
    throw new RangeError(
      `reserved bits 12-15 must be zero, got 0x${packed.toString(16)} — ` +
      `refusing to silently ignore a field this engine does not understand`,
    );
  }
  return { dir: packed & 0xff, mag: (packed >>> 8) & 0x0f };
}

/**
 * Nearest direction index for a vector, by maximum dot product against
 * the generated unit table.
 *
 * Deliberately NOT atan2. atan2 is implementation-approximated, so the
 * same physical stick position could quantize to different steps on
 * different browsers and the same player would produce different runs on
 * different devices. A dot-product argmax uses only multiply and compare,
 * both exactly specified, and reuses the one table the whole project
 * already agrees on. 256 iterations per frame is free at 60Hz.
 */
export function quantizeDir(dirTable: Float64Array, x: number, y: number): number {
  let best = 0;
  let bestDot = -Infinity;
  for (let i = 0; i < DIR_STEPS; i++) {
    const dot = x * dirTable[i * 2]! + y * dirTable[i * 2 + 1]!;
    if (dot > bestDot) { bestDot = dot; best = i; }
  }
  return best;
}

/**
 * Quantize an analog stick or keyboard vector into the input domain.
 * Both control schemes MUST go through here, or the same player produces
 * different runs on different devices.
 */
export function quantize(dirTable: Float64Array, x: number, y: number): { dir: number; mag: number } {
  const lenSq = x * x + y * y;
  if (lenSq === 0) return { dir: 0, mag: 0 };
  const len = Math.sqrt(lenSq);
  const clamped = len > 1 ? 1 : len;
  return { dir: quantizeDir(dirTable, x, y), mag: Math.round(clamped * MAG_MAX) };
}
